// src/components/PrioritySelect.tsx
// Inline priority selector with dropdown menu showing colored priority icons.

import { useState, useRef, useEffect, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@core/ui';
import { useI18n } from '@react/hooks';
import {
  taskPriorityMap,
  taskPriorityOptions,
  getStatusMapping,
  getPriorityIconPath,
  getPriorityColor,
} from '@/features/task/logic';

export interface PrioritySelectProps {
  /** Current priority value (e.g. 'TASK_PRIORITY_HIGH') */
  value: string;
  /** Callback when a new priority is selected */
  onSelect: (priority: string) => void;
  /** Disable the selector */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
}

function PriorityIcon({ priority, className }: { priority: string; className?: string }) {
  const iconPath = getPriorityIconPath(priority);
  const colorClass = getPriorityColor(priority);
  if (!iconPath) return null;

  return (
    <svg
      className={cn('h-4 w-4 shrink-0', colorClass, className)}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={iconPath} />
    </svg>
  );
}

export function PrioritySelect({
  value,
  onSelect,
  disabled = false,
  className,
}: PrioritySelectProps) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const menuId = useId();

  const currentMapping = getStatusMapping(taskPriorityMap, value);

  const close = useCallback(() => {
    setOpen(false);
    setFocusedIndex(-1);
  }, []);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const maxH = 240;
    const openAbove = spaceBelow < maxH && rect.top > spaceBelow;

    setDropdownStyle({
      position: 'fixed',
      left: rect.left,
      minWidth: rect.width,
      width: 'max-content',
      ...(openAbove
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
      maxHeight: Math.min(maxH, openAbove ? rect.top - 8 : spaceBelow - 8),
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePosition();

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        listRef.current && !listRef.current.contains(target)
      ) {
        close();
      }
    };
    const handleScroll = () => updatePosition();

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [open, close, updatePosition]);

  useEffect(() => {
    if (open && listRef.current && focusedIndex >= 0) {
      const item = listRef.current.children[focusedIndex] as HTMLElement | undefined;
      item?.scrollIntoView({ block: 'nearest' });
    }
  }, [focusedIndex, open]);

  const handleSelect = (priorityValue: string) => {
    onSelect(priorityValue);
    close();
    triggerRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (!open) {
          setOpen(true);
          const idx = taskPriorityOptions.findIndex((o) => o.value === value);
          setFocusedIndex(idx >= 0 ? idx : 0);
        } else if (focusedIndex >= 0) {
          handleSelect(taskPriorityOptions[focusedIndex].value);
        }
        break;
      case 'Escape':
        e.preventDefault();
        close();
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!open) {
          setOpen(true);
          const idx = taskPriorityOptions.findIndex((o) => o.value === value);
          setFocusedIndex(idx >= 0 ? idx : 0);
        } else {
          setFocusedIndex((prev) =>
            prev + 1 < taskPriorityOptions.length ? prev + 1 : prev,
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (open) {
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        }
        break;
    }
  };

  const dropdown = open
    ? createPortal(
        <ul
          ref={listRef}
          id={menuId}
          role="listbox"
          aria-label={t('task.field.priority')}
          aria-activedescendant={
            focusedIndex >= 0 ? `${menuId}-option-${focusedIndex}` : undefined
          }
          style={dropdownStyle}
          className={cn(
            'z-50 overflow-auto rounded-md border py-1 shadow-lg',
            'bg-popover text-popover-foreground border-border',
          )}
        >
          {taskPriorityOptions.map((option, index) => {
            const isSelected = option.value === value;
            const isFocused = index === focusedIndex;
            return (
              <li
                key={option.value}
                id={`${menuId}-option-${index}`}
                role="option"
                aria-selected={isSelected}
                onMouseEnter={() => setFocusedIndex(index)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(option.value);
                }}
                className={cn(
                  'cursor-pointer select-none px-3 py-1.5 text-sm flex items-center gap-2',
                  isFocused && 'bg-accent text-accent-foreground',
                  isSelected && !isFocused && 'bg-accent/50',
                )}
              >
                <PriorityIcon priority={option.value} />
                <span>{t(option.labelKey)}</span>
                {isSelected && (
                  <svg className="h-4 w-4 ml-auto text-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </li>
            );
          })}
        </ul>,
        document.body,
      )
    : null;

  return (
    <div className={cn('inline-flex', className)}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!disabled) {
            setOpen((prev) => !prev);
            if (!open) {
              const idx = taskPriorityOptions.findIndex((o) => o.value === value);
              setFocusedIndex(idx >= 0 ? idx : 0);
            }
          }
        }}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('task.field.priority')}
        className={cn(
          'w-full inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-2 text-sm',
          'hover:bg-muted/50 transition-colors',
          open ? 'ring-2 ring-ring' : '',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'disabled:opacity-50 disabled:cursor-not-allowed',
        )}
      >
        <PriorityIcon priority={value} />
        <span className="text-foreground flex-1 text-left">{t(currentMapping.labelKey)}</span>
        <svg
          className={cn(
            'h-3.5 w-3.5 text-muted-foreground transition-transform flex-shrink-0',
            open && 'rotate-180',
          )}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {dropdown}
    </div>
  );
}
